import React, { useEffect, useMemo, useState } from 'react';
import SignalIntelligencePanel from './SignalIntelligencePanel';
import ConversationRegister from './ConversationRegister';
import IndonesiaChoropleth from './IndonesiaChoropleth';
import Plot from 'react-plotly.js';
import { InsightActions, ClipButton } from './ReportWorkspace';
import { PageTabs, StatusKey } from './PageGuide';
import '../styles/DecisionIntelligenceViews.css';
import '../styles/FieldRefinements.css';
import '../styles/GeoAndCompany.css';
import {
  brandStatus,
  fieldStatus,
  BRAND_MIN_SCORED,
  FIELD_MIN_CONVERSATIONS,
  STATUS_COLOUR,
  STATUS_TEXT,
} from '../utils/evidenceStatus';
import { brandProvinceValues, fieldProvinceValues } from '../utils/provinceValues';
const EMPTY = [];
const Empty = ({ children }) => <div className="decision-empty">{children}</div>;
// Brand rows carry net_sentiment; everything else is a field (location x crop) item.
const classify = (row) => (row?.net_sentiment != null ? brandStatus(row) : fieldStatus(row));
const status = (row) => classify(row);
const statusText = (row) => STATUS_TEXT[classify(row)];
const colour = (row) => STATUS_COLOUR[classify(row)];
const issueColours = ['#c37637', '#8b62a8', '#418a72', '#3478a2', '#b85667', '#8a7b36', '#5e6fb1', '#a16484'];
const issueColour = (name) =>
  issueColours[[...String(name || '')].reduce((sum, ch) => sum + ch.charCodeAt(0), 0) % issueColours.length];
function Heatmap({ rows, columns, valueFor, onSelect, suffix = '' }) {
  return (
    <div className="chicklet-wrap">
      <table className="chicklet-table">
        <thead>
          <tr>
            <th></th>
            {columns.map((c) => (
              <th key={c}>{c}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r}>
              <th>{r}</th>
              {columns.map((c) => {
                const x = valueFor(r, c),
                  v = x?.value;
                return (
                  <td key={c}>
                    <button
                      className={x?.selected ? 'selected' : ''}
                      disabled={v == null}
                      onClick={() => v != null && onSelect?.(x)}
                      style={{
                        background: colour(x),
                      }}
                      title={x?.label || ''}
                    >
                      <b>{v == null ? '–' : `${v}${suffix || (x?.brand && x?.region ? '%' : '')}`}</b>
                      <small>{x?.count ? `${x.count} conversations` : ''}</small>
                    </button>
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
function Priorities({ rows, activeKey, keyFor, titleFor, detailFor, valueFor, onSelect }) {
  return (
    <div className="priority-table">
      {rows.map((x) => {
        const k = keyFor(x),
          v = valueFor(x);
        return (
          <button key={k} className={k === activeKey ? 'active' : ''} onClick={() => onSelect(x)}>
            <i
              style={{
                background: colour({
                  ...x,
                  urgency: v,
                }),
              }}
            />
            <span>
              <b>{titleFor(x)}</b>
              <small>{detailFor(x)}</small>
            </span>
            <strong>
              {statusText({
                ...x,
                urgency: v,
              })}
            </strong>
          </button>
        );
      })}
    </div>
  );
}
function ChangeBoard({ rows, onSelect, nameFor }) {
  return (
    <div className="change-board">
      {['emerging', 'current', 'dying', 'insufficient history'].map((group) => (
        <section key={group}>
          <h4>{group === 'dying' ? 'Declining' : group}</h4>
          {rows
            .filter((x) => x.lifecycle === group)
            .slice(0, 8)
            .map((x) => {
              const series = (x.timeline || []).map((t) => Number(t.conversations || 0));
              const peak = Math.max(1, ...series);
              const points = series
                .map((v, i) => `${(i * 70) / Math.max(1, series.length - 1)},${24 - (v * 22) / peak}`)
                .join(' ');
              return (
                <button key={nameFor(x)} onClick={() => onSelect(x)}>
                  <b>{nameFor(x)}</b>
                  {series.length > 1 && (
                    <svg
                      className="change-sparkline"
                      viewBox="0 0 70 28"
                      role="img"
                      aria-label="Mentions over time"
                    >
                      <polyline points={points} fill="none" stroke="#27805a" strokeWidth="2" />
                    </svg>
                  )}
                  <span>
                    {x.change_percent == null
                      ? 'Limited history'
                      : `${x.change_percent > 0 ? '+' : ''}${x.change_percent}%`}
                  </span>
                </button>
              );
            })}
          {!rows.some((x) => x.lifecycle === group) && <small>None in this selection</small>}
        </section>
      ))}
    </div>
  );
}
export function BrandDecisionView({ data, filters, loading }) {
  const brands = data?.brands || EMPTY,
    [selected, setSelected] = useState(''),
    [brandRegion, setBrandRegion] = useState(''),
    [movementView, setMovementView] = useState('table'),
    [tab, setTab] = useState('overview');
  useEffect(() => {
    if (brands.length && !brands.some((x) => x.brand === selected)) setSelected(brands[0].brand);
  }, [brands, selected]);
  const active = brands.find((x) => x.brand === selected) || brands[0];
  const heat = useMemo(() => {
    const raw = data?.region_sentiment || EMPTY,
      top = brands.slice(0, 10).map((x) => x.brand),
      totals = {},
      b = {};
    raw.forEach((x) => {
      const a = x.state || x.region_name,
        n = Number(x.count || 0),
        s = String(x.sentiment || '').toLowerCase();
      totals[a] = (totals[a] || 0) + n;
      const k = `${a}|${x.entity_name}`;
      b[k] = b[k] || {
        positive: 0,
        negative: 0,
        mixed: 0,
        neutral: 0,
        total: 0,
      };
      if (b[k][s] != null) b[k][s] += n;
      b[k].total += n;
    });
    const regions = Object.entries(totals)
        .sort((a, z) => z[1] - a[1])
        .map((x) => x[0]),
      lookup = {};
    regions.forEach((r) =>
      top.forEach((c) => {
        const x = b[`${r}|${c}`];
        if (!x?.total) return;
        const positive = Math.round((x.positive * 100) / x.total),
          negative = Math.round((x.negative * 100) / x.total),
          net = positive - negative;
        lookup[`${r}|${c}`] = {
          value: net,
          net_sentiment: net,
          total: x.total,
          count: x.total,
          brand: c,
          region: r,
          label: `${positive}% positive · ${negative}% negative · ${x.mixed} mixed assessments`,
        };
      }),
    );
    return {
      regions,
      brands: top,
      lookup,
    };
  }, [data, brands]);
  if (loading) return <Empty>Loading brand intelligence…</Empty>;
  if (!active) return <Empty>No brand sentiment has been scored for this selection.</Empty>;
  const report = {
    page: 'Brand Intelligence',
    title: `${active.brand}: current market view`,
    subtitle: 'Sentiment, context and supporting evidence',
    finding: `${active.brand} has net sentiment ${active.net_sentiment}; ${active.total} scored conversations support this view.`,
    next_check: active.next_step,
    rows: brands.slice(0, 10).map((x) => ({
      label: `${x.brand} · ${x.main_context}`,
      value: `${x.net_sentiment > 0 ? '+' : ''}${x.net_sentiment} net sentiment`,
      status: status(x),
    })),
  };
  const mapValues = brandProvinceValues(data?.region_sentiment, active.brand);
  // The selected province's own figures, so they are never mistaken for the national ones.
  const regionValue = brandRegion ? mapValues.find((x) => x.province === brandRegion) : null;
  const regionCell = regionValue && { ...regionValue, total: regionValue.count, net_sentiment: regionValue.value };
  const overview = (
    <>
      <section className="decision-pane decision-wide">
        <header>
          <div>
            <h3>Brand sentiment by province</h3>
            <p>
              Select a brand or province to inspect the underlying context. Grey means no scored
              conversations.
            </p>
          </div>
          <ClipButton
            item={{
              ...report,
              title: 'Brand sentiment by province',
              visual: 'table',
              rows: heat.regions.slice(0, 7).map((r) => ({
                label: r,
                value: `${Object.values(heat.lookup)
                  .filter((x) => x.region === r)
                  .reduce((n, x) => n + x.count, 0)} scored conversations`,
                status: 'none',
              })),
            }}
          />
        </header>
        <Heatmap
          rows={brandRegion ? heat.regions.filter((x) => x === brandRegion) : heat.regions}
          columns={heat.brands}
          valueFor={(r, c) => {
            const x = heat.lookup[`${r}|${c}`];
            return x
              ? {
                  ...x,
                  selected: c === active.brand,
                }
              : null;
          }}
          onSelect={(x) => {
            setSelected(x.brand);
            setBrandRegion(x.region);
          }}
        />
        <div className="selected-summary">
          <b>{active.brand}</b>
          <span>
            All Indonesia: net sentiment {active.net_sentiment > 0 ? '+' : ''}
            {active.net_sentiment} · {active.total} scored conversations
          </span>
          {brandRegion && (
            <span>
              {brandRegion}:{' '}
              {regionCell
                ? `net sentiment ${regionCell.value > 0 ? '+' : ''}${regionCell.value} · ${regionCell.count} scored conversation${regionCell.count === 1 ? '' : 's'}${status(regionCell) === 'grey' ? ' (not enough evidence)' : ''}`
                : 'no scored conversations'}
            </span>
          )}
          <span>{active.main_context}</span>
          <button onClick={() => setBrandRegion('')}>All provinces</button>
        </div>
      </section>
      <section className="decision-pane decision-wide">
        <header>
          <div>
            <h3>{active.brand} across Indonesia</h3>
            <p>
              Province color shows net sentiment (positive minus negative, as a percentage of scored
              conversations).
            </p>
          </div>
          <ClipButton
            item={{
              ...report,
              title: `${active.brand} across Indonesia`,
              visual: 'map',
              map_values: mapValues,
              map_metric: 'Net sentiment',
              map_palette: 'sentiment',
              map_min_count: BRAND_MIN_SCORED,
            }}
          />
        </header>
        <IndonesiaChoropleth
          values={mapValues}
          metric="Net sentiment"
          palette="sentiment"
          minCount={BRAND_MIN_SCORED}
          selected={brandRegion}
          onSelect={setBrandRegion}
        />
      </section>
      <section className="decision-pane">
        <header>
          <div>
            <h3>Brand status</h3>
            <p>Select a brand to compare.</p>
          </div>
          <ClipButton item={{ ...report, title: 'Brand status', visual: 'table' }} />
        </header>
        <Priorities
          rows={brands.slice(0, 14)}
          activeKey={active.brand}
          keyFor={(x) => x.brand}
          titleFor={(x) => x.brand}
          detailFor={(x) => `${x.main_context || 'No crop context'} · ${x.total} conversations`}
          valueFor={(x) => x.urgency}
          onSelect={(x) => setSelected(x.brand)}
        />
      </section>
    </>
  );
  return (
    <div className="decision-view">
      <div className="decision-toolbar">
        <PageTabs
          active={tab}
          onChange={setTab}
          items={[
            {
              id: 'overview',
              label: 'Overview',
            },
            {
              id: 'movement',
              label: 'What is changing',
            },
          ]}
        />
        <div>
          <StatusKey />
          <InsightActions item={report} />
        </div>
      </div>
      {tab === 'movement' ? (
        <div className="decision-workspace expanded-context-workspace">
          <section className="decision-pane decision-wide">
            <header>
              <div>
                <h3>Brand movement</h3>
                <p>Conversation mentions over time, not sales performance.</p>
              </div>
              <div className="field-view-toggle">
                <button
                  className={movementView === 'table' ? 'active' : ''}
                  onClick={() => setMovementView('table')}
                >
                  Table
                </button>
                <button
                  className={movementView === 'chart' ? 'active' : ''}
                  onClick={() => setMovementView('chart')}
                >
                  Chart
                </button>
                <ClipButton
                  item={{
                    ...report,
                    title: `Brand movement${movementView === 'chart' ? ` · ${active.brand}` : ''}`,
                    visual: movementView === 'chart' ? 'timeline' : 'table',
                    timeline: active.timeline,
                    rows: brands.slice(0, 7).map((x) => ({
                      label: x.brand,
                      value: `${x.change_percent == null ? 'Limited' : `${x.change_percent}%`} change`,
                      status: status(x),
                    })),
                  }}
                />
              </div>
            </header>
            {movementView === 'table' ? (
              <ChangeBoard rows={brands} nameFor={(x) => x.brand} onSelect={(x) => setSelected(x.brand)} />
            ) : (
              <Plot
                data={brands.slice(0, 6).map((x, i) => ({
                  type: 'scatter',
                  mode: 'lines+markers',
                  name: x.brand,
                  x: (x.timeline || []).map((p) => p.period),
                  y: (x.timeline || []).map((p) => p.conversations),
                  line: { color: ['#2457a7', '#27805a', '#c48921', '#be6752', '#7559a4', '#597b83'][i] },
                }))}
                layout={{
                  autosize: true,
                  height: 370,
                  margin: { l: 45, r: 25, t: 20, b: 60 },
                  yaxis: { title: 'Conversations' },
                  legend: { orientation: 'h', y: -0.25 },
                  paper_bgcolor: 'transparent',
                  plot_bgcolor: 'transparent',
                }}
                config={{ displayModeBar: false, responsive: true }}
                useResizeHandler
                style={{ width: '100%' }}
              />
            )}
          </section>
          <SignalIntelligencePanel embedded fullWidth entityName={active.brand} filters={filters} />
        </div>
      ) : (
        <div className="decision-workspace">
          {overview}
          <SignalIntelligencePanel embedded fullWidth entityName={active.brand} filters={filters} />
        </div>
      )}
      <p className="decision-method">{data?.method_note}</p>
    </div>
  );
}
export function RegionCropDecisionView({ data, filters, loading }) {
  const cells = data?.cells || EMPTY,
    issues = data?.issues || EMPTY,
    [selected, setSelected] = useState(null),
    [province, setProvince] = useState(''),
    [theme, setTheme] = useState(''),
    [overviewView, setOverviewView] = useState('table'),
    [tab, setTab] = useState('overview');
  useEffect(() => {
    if (cells.length && !selected) setSelected(cells[0]);
  }, [cells, selected]);
  const provinces = useMemo(() => [...new Set(cells.map((x) => x.state).filter(Boolean))].sort(), [cells]);
  const visible = cells.filter((x) => !province || x.state === province),
    visibleIssues = issues.filter(
      (x) => (!province || x.state === province) && (!theme || x.theme === theme),
    ),
    active = selected || visible[0] || cells[0];
  const heat = useMemo(() => {
    const source = province ? cells.filter((x) => x.state === province) : cells,
      g = {};
    source.forEach((x) => {
      const a = province ? x.region_name : x.state || x.region_name,
        k = `${a}|${x.crop}`,
        z = g[k] || {
          ...x,
          region_name: a,
          urgency: 0,
          conversations: 0,
          contributors: 0,
          need_signals: 0,
        };
      z.urgency = Math.max(z.urgency, x.urgency);
      z.conversations += x.conversations;
      z.contributors = Math.max(z.contributors, x.contributors);
      z.need_signals += x.need_signals;
      g[k] = z;
    });
    const list = Object.values(g),
      rt = {},
      ct = {};
    list.forEach((x) => {
      rt[x.region_name] = (rt[x.region_name] || 0) + x.conversations;
      ct[x.crop] = (ct[x.crop] || 0) + x.conversations;
    });
    return {
      regions: Object.entries(rt)
        .sort((a, z) => z[1] - a[1])
        .map((x) => x[0]),
      crops: Object.entries(ct)
        .sort((a, z) => z[1] - a[1])
        .slice(0, 12)
        .map((x) => x[0]),
      lookup: Object.fromEntries(
        list.map((x) => [
          `${x.region_name}|${x.crop}`,
          {
            ...x,
            value: x.urgency,
            count: x.conversations,
          },
        ]),
      ),
    };
  }, [cells, province]);
  if (loading) return <Empty>Loading field intelligence…</Empty>;
  if (!active) return <Empty>No location and crop evidence exists for this selection.</Empty>;
  const report = {
    page: 'Field Intelligence',
    title: `${province || 'Indonesia'} field priorities`,
    subtitle: 'Location, crop and supporting evidence',
    finding: `${active.region_name} → ${active.crop} is supported by ${active.conversations} conversations from ${active.contributors} contributors.`,
    next_check:
      'Review the comments with the regional team, then compare with availability and sales activity.',
    rows: visible.slice(0, 12).map((x) => ({
      label: `${x.region_name} · ${x.crop}`,
      value: `${statusText(x)} · ${x.conversations} conversations`,
      status: status(x),
    })),
  };
  if (tab === 'issues') {
    const issueActive = selected?.issue ? selected : visibleIssues[0],
      sameIssue = visibleIssues
        .filter((x) => x.issue === issueActive?.issue)
        .sort((a, b) => b.conversations - a.conversations)
        .slice(0, 8),
      max = Math.max(...sameIssue.map((x) => Number(x.conversations || 0)), 1);
    const issueGroups = Object.values(
      visibleIssues.reduce((acc, x) => {
        const key = `${String(x.issue).toLowerCase()}|${x.issue_type}`;
        acc[key] = acc[key] || {
          issue: x.issue,
          issue_type: x.issue_type,
          theme: x.theme,
          conversations: 0,
          locations: new Set(),
          crops: new Set(),
        };
        acc[key].conversations += Number(x.conversations || 0);
        acc[key].locations.add(x.region_name);
        acc[key].crops.add(x.crop);
        return acc;
      }, {}),
    ).sort((a, b) => b.conversations - a.conversations);
    return (
      <div className="decision-view">
        <div className="decision-toolbar">
          <PageTabs
            active={tab}
            onChange={(x) => {
              setTab(x);
              setSelected(null);
            }}
            items={[
              {
                id: 'overview',
                label: 'Overview',
              },
              {
                id: 'issues',
                label: 'Issues',
              },
              {
                id: 'trends',
                label: 'What is changing',
              },
              {
                id: 'conversations',
                label: 'Conversations',
              },
            ]}
          />
          <div>
            <StatusKey />
            <InsightActions item={report} />
          </div>
        </div>
        <div className="context-row">
          <label>
            Geography
            <select
              value={province}
              onChange={(e) => {
                setProvince(e.target.value);
                setSelected(null);
              }}
            >
              <option value="">All provinces / states</option>
              {provinces.map((p) => (
                <option key={p}>{p}</option>
              ))}
            </select>
          </label>
          <label>
            Issue theme
            <select
              value={theme}
              onChange={(e) => {
                setTheme(e.target.value);
                setSelected(null);
              }}
            >
              <option value="">All recorded themes</option>
              {(data?.issue_themes || []).map((value) => (
                <option key={value}>{value}</option>
              ))}
            </select>
          </label>
          <span>Weather and supply issues need taxonomy coverage before they can appear here.</span>
        </div>
        <div className="decision-workspace">
          <section className="decision-pane">
            <header>
              <div>
                <h3>Problems being reported</h3>
                <p>Each issue appears once. Select it to see its crops and locations.</p>
              </div>
              <ClipButton
                item={{
                  ...report,
                  title: 'Problems being reported',
                  visual: 'table',
                  rows: issueGroups.slice(0, 7).map((x) => ({
                    label: x.issue,
                    value: `${x.conversations} crop-location mentions`,
                    status: 'none',
                  })),
                }}
              />
            </header>
            <div className="issue-group-list">
              {issueGroups.slice(0, 25).map((x) => (
                <button
                  key={`${x.issue}|${x.issue_type}`}
                  style={{ '--issue-color': issueColour(x.issue) }}
                  className={issueActive?.issue === x.issue ? 'active' : ''}
                  onClick={() => setSelected(x)}
                >
                  <i />
                  <span>
                    <b>{x.issue}</b>
                    <small>
                      {x.theme} · {x.locations.size} locations · {x.crops.size} crops
                    </small>
                  </span>
                  <strong>{x.conversations} mentions</strong>
                </button>
              ))}
            </div>
          </section>
          <section className="decision-pane">
            <header>
              <div>
                <h3>{issueActive?.issue || 'Issue'} by crop and location</h3>
                <p>Compare where the selected issue is appearing most often.</p>
              </div>
              <ClipButton
                item={{
                  ...report,
                  title: `${issueActive?.issue || 'Issue'} by crop and location`,
                  visual: 'bars',
                  rows: sameIssue.map((x) => ({
                    label: `${x.region_name} · ${x.crop}`,
                    value: `${x.conversations} conversations`,
                    status: 'none',
                  })),
                }}
              />
            </header>
            {sameIssue.length ? (
              <div className="issue-bars">
                {sameIssue.map((x) => (
                  <button key={`${x.region_id}|${x.crop}`} onClick={() => setSelected(x)}>
                    <span>
                      {x.region_name} · {x.crop}
                    </span>
                    <i>
                      <b
                        style={{
                          width: `${Math.max(6, (Number(x.conversations) * 100) / max)}%`,
                          background: issueColour(issueActive?.issue),
                        }}
                      />
                    </i>
                    <strong>{x.conversations}</strong>
                  </button>
                ))}
              </div>
            ) : (
              <Empty>No reported issues in this selection.</Empty>
            )}
          </section>
          <SignalIntelligencePanel
            embedded
            entityName={issueActive?.issue}
            filters={{
              ...filters,
              region_id: issueActive?.region_id,
            }}
          />
        </div>
        <p className="decision-method">
          Co-occurrence means the issue and crop appeared in the same conversation; review the evidence before
          assuming causality.
        </p>
      </div>
    );
  }
  return (
    <div className="decision-view">
      <div className="decision-toolbar">
        <PageTabs
          active={tab}
          onChange={setTab}
          items={[
            {
              id: 'overview',
              label: 'Overview',
            },
            {
              id: 'issues',
              label: 'Issues',
            },
            {
              id: 'trends',
              label: 'What is changing',
            },
            {
              id: 'conversations',
              label: 'Conversations',
            },
          ]}
        />
        <div>
          <StatusKey />
          <InsightActions item={report} />
        </div>
      </div>
      <div className="context-row">
        <label>
          Geography
          <select
            value={province}
            onChange={(e) => {
              setProvince(e.target.value);
              setSelected(null);
            }}
          >
            <option value="">All provinces / states</option>
            {provinces.map((p) => (
              <option key={p}>{p}</option>
            ))}
          </select>
        </label>
        <span>
          {tab === 'overview'
            ? 'Location → crop view'
            : tab === 'issues'
              ? 'Issues requiring review'
              : tab === 'trends'
                ? 'Emerging, current and declining'
                : 'Comments and related topics'}
        </span>
      </div>
      {tab === 'trends' ? (
        <div className="decision-workspace expanded-context-workspace">
          <section className="decision-pane decision-wide">
            <header>
              <h3>How crop issues are moving</h3>
              <p>Select any item to review the underlying conversations.</p>
            </header>
            <ChangeBoard
              rows={visible}
              nameFor={(x) => `${x.region_name} → ${x.crop}`}
              onSelect={setSelected}
            />
          </section>
          <SignalIntelligencePanel
            embedded
            fullWidth
            entityName={active.crop}
            filters={{
              ...filters,
              region_id: active.region_id,
            }}
          />
        </div>
      ) : tab === 'conversations' ? (
        <ConversationRegister filters={filters} />
      ) : (
        <section className="decision-pane field-overview">
          <header>
            <div>
              <h3>Field priorities by location and crop</h3>
              <p>
                Start with the ranked table. Switch to the matrix only when comparing many locations and
                crops.
              </p>
            </div>
            <div className="field-view-toggle">
              <button
                className={overviewView === 'table' ? 'active' : ''}
                onClick={() => setOverviewView('table')}
              >
                Table
              </button>
              <button
                className={overviewView === 'matrix' ? 'active' : ''}
                onClick={() => setOverviewView('matrix')}
              >
                Comparison matrix
              </button>
            </div>
          </header>
          {overviewView === 'table' ? (
            <div className="field-priority-table">
              <div className="field-priority-head">
                <span>Location</span>
                <span>Crop</span>
                <span>Conversations</span>
                <span>Contributors</span>
                <span>Issue mentions</span>
                <span>Movement</span>
                <span>Status</span>
              </div>
              {[...visible]
                .sort((a, b) => b.urgency - a.urgency)
                .slice(0, 35)
                .map((item) => (
                  <button
                    key={`${item.region_id}|${item.crop}`}
                    onClick={() => setSelected(item)}
                    className={
                      active.region_id === item.region_id && active.crop === item.crop ? 'active' : ''
                    }
                  >
                    <span>
                      {item.region_name}
                      <small>{item.state}</small>
                    </span>
                    <span>{item.crop}</span>
                    <span>{item.conversations}</span>
                    <span>{item.contributors}</span>
                    <span>{item.need_signals}</span>
                    <span>
                      {item.lifecycle === 'dying'
                        ? 'Declining'
                        : item.lifecycle === 'emerging'
                          ? 'Increasing'
                          : 'Stable / limited'}
                    </span>
                    <span className={`field-status ${status(item)}`}>{statusText(item)}</span>
                  </button>
                ))}
            </div>
          ) : (
            <Heatmap
              rows={heat.regions}
              columns={heat.crops}
              valueFor={(r, c) => {
                const x = heat.lookup[`${r}|${c}`];
                return x
                  ? {
                      ...x,
                      selected: x.region_id === active.region_id && x.crop === active.crop,
                    }
                  : null;
              }}
              onSelect={setSelected}
            />
          )}
          <div className="selected-summary">
            <b>
              {active.region_name} → {active.crop}
            </b>
            <span>{statusText(active)}</span>
            <span>{active.need_signals} issue mentions</span>
            <span>{active.lifecycle}</span>
          </div>
        </section>
      )}
      {tab === 'overview' && (
        <section className="decision-pane field-overview">
          <header>
            <div>
              <h3>Where issues are recorded</h3>
              <p>
                Issue mentions per 100 crop conversations by province; grey means no matching conversations.
              </p>
            </div>
            <ClipButton
              item={{
                ...report,
                title: 'Field issues across Indonesia',
                visual: 'map',
                map_values: fieldProvinceValues(cells),
                map_metric: 'Issue mentions / 100 crop conversations',
                map_min_count: FIELD_MIN_CONVERSATIONS,
              }}
            />
          </header>
          <IndonesiaChoropleth
            values={fieldProvinceValues(cells)}
            metric="Issue mentions / 100 conversations"
            minCount={FIELD_MIN_CONVERSATIONS}
            selected={province}
            onSelect={(name) => {
              setProvince(name);
              setSelected(null);
            }}
          />
        </section>
      )}
      {tab === 'trends' && (
        <section className="decision-pane field-overview">
          <header>
            <div>
              <h3>
                {active.region_name} · {active.crop} over time
              </h3>
              <p>
                Conversation and issue mentions by week; a rise can also reflect changing recording coverage.
              </p>
            </div>
            <ClipButton
              item={{
                ...report,
                title: `${active.crop} in ${active.region_name} over time`,
                visual: 'timeline',
                timeline: active.timeline,
              }}
            />
          </header>
          <Plot
            data={[
              {
                type: 'scatter',
                mode: 'lines+markers',
                name: 'Conversations',
                x: (active.timeline || []).map((x) => x.period),
                y: (active.timeline || []).map((x) => x.conversations),
                line: { color: '#2457a7' },
              },
              {
                type: 'scatter',
                mode: 'lines+markers',
                name: 'Issue mentions',
                x: (active.timeline || []).map((x) => x.period),
                y: (active.timeline || []).map((x) => x.need_signals),
                line: { color: '#c75b4b' },
              },
            ]}
            layout={{
              autosize: true,
              height: 320,
              margin: { l: 45, r: 20, t: 20, b: 55 },
              legend: { orientation: 'h', y: -0.25 },
              paper_bgcolor: 'transparent',
              plot_bgcolor: 'transparent',
            }}
            config={{ displayModeBar: false, responsive: true }}
            useResizeHandler
            style={{ width: '100%' }}
          />
        </section>
      )}
      <p className="decision-method">{data?.method_note}</p>
    </div>
  );
}
